import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignProviderCampaignEntity } from './campaign-provider-campaign.entity';
import { CampaignEntity } from './campaign.entity';

export type CampaignMembershipState =
	| 'active'
	| 'replied'
	| 'completed'
	| 'unsubscribed'
	| 'bounced'
	| 'suppressed'
	| 'fell_out';

export type CampaignReplySentiment = 'positive' | 'negative' | 'neutral';

/**
 * One contact's membership in a Workd campaign. This is the spine of the call /
 * follow-up lists: reps filter on `externalStatus` (the provider's per-contact
 * lead status) plus the denormalized communication signals below.
 *
 * The comm-signal columns (emailCount, hasReplied, lastEmailSentDate, ...) are
 * denormalized from CampaignMessage by the sync so list filters stay fast.
 *
 * A contact is in a Workd campaign once, and in exactly one provider campaign
 * (`fkCampaignProviderCampaignId`). Contact/client links are nullable so the row
 * can exist while identity resolution (lead email -> client_contact) is pending;
 * `emailAddress` is the resolution key in that window.
 *
 * Not auditable: high-volume, sync-maintained.
 */
@Index('campaign_membership__fk_campaign', ['fkCampaignId'], {})
@Index('campaign_membership__fk_contact', ['fkClientContactId'], {})
@Index('campaign_membership__external_lead', ['externalLeadId'], {})
@Index('campaign_membership__state', ['fkCampaignId', 'state'], {})
@Entity('campaign_membership', {}, { auditable: false })
export class CampaignMembershipEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	@Column('int', {
		name: 'fk_campaign_provider_campaign__id',
		unsigned: true,
		nullable: true,
	})
	public fkCampaignProviderCampaignId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public fkClientId!: number | null;

	@Column('int', {
		name: 'fk_client_contact__id',
		unsigned: true,
		nullable: true,
	})
	public fkClientContactId!: number | null;

	@Column('varchar', { name: 'externalLeadId', length: 255, nullable: true })
	public externalLeadId!: string | null;

	@Column('varchar', { name: 'emailAddress', length: 255, nullable: true })
	public emailAddress!: string | null;

	@Column('varchar', { name: 'state', length: 50, default: () => "'active'" })
	public state!: CampaignMembershipState | string;

	// The provider's per-contact lead status (e.g. Instantly: interested,
	// not_interested, meeting_booked, ...). The primary filter field for lists.
	@Column('varchar', { name: 'externalStatus', length: 100, nullable: true })
	public externalStatus!: string | null;

	@Column('varchar', { name: 'replySentiment', length: 20, nullable: true })
	public replySentiment!: CampaignReplySentiment | string | null;

	@Column('int', { name: 'emailCount', default: () => '0' })
	public emailCount!: number;

	@Column('datetime', { name: 'lastEmailSentDate', nullable: true })
	public lastEmailSentDate!: Date | null;

	@Column('tinyint', { name: 'hasReplied', width: 1, default: () => "'0'" })
	public hasReplied!: boolean;

	@Column('datetime', { name: 'lastInboundDate', nullable: true })
	public lastInboundDate!: Date | null;

	@Column('datetime', { name: 'lastCallDate', nullable: true })
	public lastCallDate!: Date | null;

	@Column('varchar', { name: 'callOutcome', length: 100, nullable: true })
	public callOutcome!: string | null;

	// Any channel — used as a cooldown guard when recycling contacts through offers.
	@Column('datetime', { name: 'lastContactedDate', nullable: true })
	public lastContactedDate!: Date | null;

	@Column('datetime', { name: 'enrolledDate', nullable: true })
	public enrolledDate!: Date | null;

	@Column('datetime', { name: 'lastSyncedAt', nullable: true })
	public lastSyncedAt!: Date | null;

	@ManyToOne(() => CampaignEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign__id', referencedColumnName: 'id' }])
	public fkCampaign!: CampaignEntity;

	@ManyToOne(() => CampaignProviderCampaignEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_campaign_provider_campaign__id', referencedColumnName: 'id' },
	])
	public fkCampaignProviderCampaign!: CampaignProviderCampaignEntity | null;
}
